import React, {
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Divider, Tour, TourProps } from 'antd';
import { ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useSelector } from 'react-redux';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import styles from './MyLeadsTour.module.css';
import classNames from 'classnames';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../../constants/screenSize';
import moment from 'moment';
import { TOURS_DATE_START_FROM } from '../../GetLeads/XRay/XRayLeadsTour';

export type MyLeadsTourRefs = {
  filterPanelRef: MutableRefObject<HTMLDivElement | null>;
  saveAudienceButtonRef: MutableRefObject<HTMLButtonElement | null>;
  leadRef: MutableRefObject<HTMLDivElement | null>;
};

type Props = {};

export const MyLeadsTour = React.forwardRef<MyLeadsTourRefs, Props>(
  (props, ref) => {
    const companyId = useSelector(selectActiveCompanyId);
    const currentUserId = useSelector(selectCurrentUserId);
    const session = useSelector(selectSessionSelector);
    const userAttributes = session?.userAttributes;
    const isNewUser = moment(session.createdAt) > moment(TOURS_DATE_START_FROM);

    const filterPanelRef = useRef<HTMLDivElement | null>(null);
    const saveAudienceButtonRef = useRef<HTMLButtonElement | null>(null);
    const leadRef = useRef<HTMLDivElement | null>(null);

    const { updateUserAttributes } = useUsersCRUDActions();

    const { screenWidth } = useResolutionInfo();
    const isMobile = screenWidth <= MOBILE_THRESHOLD;

    useImperativeHandle(ref, () => ({
      filterPanelRef,
      saveAudienceButtonRef,
      leadRef,
    }));
    const [open, setOpen] = useState<boolean>(false);

    const showTour = !userAttributes?.seenTour?.leads && !isMobile && isNewUser;

    const steps: TourProps['steps'] = [
      {
        title: (
          <div className={styles.stepTitle}>
            Select filters to find Audiences
          </div>
        ),
        description: (
          <div className={styles.stepDescription}>
            Filter your contacts by engagement, activity, and date.
            <Divider className={styles.divider} />
          </div>
        ),
        target: filterPanelRef.current,
        placement: 'rightTop',
        nextButtonProps: {
          children: (
            <div>
              <ArrowRightOutlined /> Next
            </div>
          ),
          className: styles.nextButton,
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
      },
      {
        title: <div className={styles.stepTitle}>Save as an audience</div>,
        description: (
          <div className={styles.stepDescription}>
            Apply filters and save the contacts in an audience. Any new contacts
            that match these filters will be added to the audience.
            <Divider className={styles.divider} />
          </div>
        ),
        target: saveAudienceButtonRef.current,
        placement: 'bottom',
        nextButtonProps: {
          children: (
            <div>
              <ArrowRightOutlined /> Next
            </div>
          ),
          className: styles.nextButton,
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
      },
      {
        title: <div className={styles.stepTitle}>Click to view lead info</div>,
        description: (
          <div className={styles.stepDescription}>
            Your contacts will appear here with customer journey data, consumer
            information, and conversion attributes.
            <Divider className={styles.divider} />
          </div>
        ),
        nextButtonProps: {
          children: (
            <div>
              <CheckOutlined /> Done
            </div>
          ),
          className: classNames(styles.nextButton, styles.doneButton),
        },
        prevButtonProps: {
          className: styles.prevButton,
        },
        target: leadRef.current,
      },
    ];

    useEffect(() => {
      if (showTour) {
        setOpen(true);
        updateUserAttributes(companyId, currentUserId, {
          seenTour: {
            ...userAttributes.seenTour,
            leads: true,
          },
        });
      }
    }, []);

    if (!open) {
      return <></>;
    }

    return (
      <Tour
        rootClassName={styles.tour}
        open={open}
        onClose={() => setOpen(false)}
        indicatorsRender={(current, total) => (
          <span className={styles.indicator}>
            {current + 1} of {total}
          </span>
        )}
        steps={steps}
        mask
      />
    );
  },
);
